import { FC, useEffect, useMemo } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { useFormatter } from '@eneco/i18n';
import { Box, Bucket, ButtonLink, Card, Heading, Image, Stack, Text } from '@eneco/sparky';
import { SuccessIcon } from '@eneco/sparky/icons';
import { styled } from '@eneco/sparky/stitches';
import { useContent } from '@sitecore/common';
import { OrderManagementWithItems } from '@sitecore/types/manual/be/OrderManagementWithItems';
import { useFormContext } from 'react-hook-form';

import { clearPriceSimulationState } from '../../price-simulation/utils/priceSimulationLocalStorage';
import { useOrderId } from '../hooks/useOrderId';
import { useSelectedProducts } from '../hooks/useSelectedProducts';
import { OrderManagementFormSchema } from '../types/orderManagementSchema';
import { FlowChildProps } from '../types/OrderManagementSteps';
import { clearOrderManagementState } from '../utils/orderManagementStorage';

type ChecklistStep = { title: string; description: string };

const ChecklistWrapper = styled('ul', {
  listStyle: 'none',
  paddingLeft: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '$6',
});

export const OrderConfirmationStep: FC<FlowChildProps> = () => {
  const { fields } = useContent<OrderManagementWithItems>();
  const { format } = useFormatter();
  const { watch } = useFormContext<OrderManagementFormSchema>();
  const orderId = useOrderId();

  const { hasSelectedElectricityProduct, hasSelectedGasProduct, hasSelectedServiceProposal, hasSelectedMaintenance } =
    useSelectedProducts({ orderId });

  const firstName = watch('personalDetailsStep.firstName');
  const reason = watch('electricityDetailsStep.reason') || watch('gasDetailsStep.reason');

  const orderConfirmationFields = fields?.orderConfirmationStep;

  useEffect(() => {
    clearOrderManagementState();
  }, []);

  const titleText = orderConfirmationFields?.title?.value
    ? format(orderConfirmationFields.title.value, { name: firstName ?? '' })
    : null;

  const checklistSteps = useMemo((): ChecklistStep[] => {
    const steps: ChecklistStep[] = [];
    const f = orderConfirmationFields;

    const emailTitle = f?.emailConfirmationTitle?.value;
    const emailDesc = f?.emailConfirmationDescription?.value;
    if (emailTitle ?? emailDesc) {
      steps.push({ title: emailTitle ?? '', description: emailDesc ?? '' });
    }

    if ((hasSelectedElectricityProduct || hasSelectedGasProduct) && reason) {
      const elTitle =
        reason === 'MoveToEneco'
          ? f?.energyMoveToEnecoTitle?.value
          : reason === 'NewMeterConnection'
            ? f?.energyNewMeterConnectionTitle?.value
            : f?.energyTakeOverMeterConnectionOldInhabitantTitle?.value;
      const elDesc =
        reason === 'MoveToEneco'
          ? f?.energyMoveToEnecoDescription?.value
          : reason === 'NewMeterConnection'
            ? f?.energyNewMeterConnectionDescription?.value
            : f?.energyTakeOverMeterConnectionOldInhabitantDescription?.value;
      if (elTitle ?? elDesc) {
        steps.push({ title: elTitle ?? '', description: elDesc ?? '' });
      }
    }

    if (hasSelectedServiceProposal) {
      const serviceTitle = f?.serviceProposalTitle?.value;
      const serviceDesc = f?.serviceProposalDescription?.value;
      if (serviceTitle ?? serviceDesc) {
        steps.push({ title: serviceTitle ?? '', description: serviceDesc ?? '' });
      }
    }

    if (hasSelectedMaintenance) {
      const maintenanceTitle = f?.boilerMaintenanceTitle?.value;
      const maintenanceDesc = f?.boilerMaintenanceDescription?.value;
      if (maintenanceTitle ?? maintenanceDesc) {
        steps.push({ title: maintenanceTitle ?? '', description: maintenanceDesc ?? '' });
      }
    }

    return steps;
  }, [
    orderConfirmationFields,
    hasSelectedElectricityProduct,
    hasSelectedGasProduct,
    hasSelectedMaintenance,
    hasSelectedServiceProposal,
    reason,
  ]);

  const onConfirmationButtonClick = () => {
    clearPriceSimulationState();
    clearOrderManagementState();
  };

  return (
    <Stack gap="10" alignX="start">
      <Stack direction="column" gap="2">
        {titleText && (
          <Heading size="M" as="h1">
            {titleText}
          </Heading>
        )}
        <div>
          <RichText html={orderConfirmationFields?.subTitle?.value} />
        </div>
      </Stack>
      <Bucket title={orderConfirmationFields?.bucketTitle?.value}>
        <Bucket.Content>
          <ChecklistWrapper>
            {checklistSteps.map((step, index) => (
              <Stack as="li" key={index} gap={2} direction="row" alignY="start">
                <SuccessIcon color="feedbackSuccess" />
                <Stack direction="column" gap={1}>
                  {step.title && <Text weight="bold">{step.title}</Text>}
                  {step.description && <RichText html={step.description} />}
                </Stack>
              </Stack>
            ))}
          </ChecklistWrapper>
        </Bucket.Content>
      </Bucket>
      <Card>
        <Box padding="6">
          <Stack direction="row" gap="6">
            <Stack.Item>
              <Image
                alt={orderConfirmationFields?.contactCardImage?.value?.alt ?? ''}
                src={orderConfirmationFields?.contactCardImage?.value?.formats[0]?.src ?? ''}
                width="160px"
                height="auto"
                objectFit="cover"
              />
            </Stack.Item>
            <Stack direction="column" gap="4" alignX="start">
              <Heading size="S" as="h2">
                {orderConfirmationFields?.contactCardTitle?.value}
              </Heading>
              <div>
                <RichText html={orderConfirmationFields?.contactCardContent?.value} />
              </div>
            </Stack>
          </Stack>
        </Box>
      </Card>
      <ButtonLink
        action="primary"
        onClick={onConfirmationButtonClick}
        href={orderConfirmationFields?.confirmationButtonLink?.value?.href ?? ''}>
        {orderConfirmationFields?.confirmationButtonLink?.value?.text}
      </ButtonLink>
    </Stack>
  );
};
